/**
 * The last few API calls that failed in this tab, so a problem report can name the
 * exact requests and the error-tracker event behind each 500 (X-Error-Id).
 *
 * Paths only: the query string is where a patient surname travels
 * (`?search=Benali`), so it never enters the buffer.
 */
export type FailedRequest = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  status: number;
  error_id?: string;
  at: string;
};

const MAX_ENTRIES = 10;
const METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
const HEX_ID = /^[0-9a-f]{32}$/;
const OWN_ENDPOINT = '/problem-reports';

const buffer: FailedRequest[] = [];

export function requestPath(url: string | undefined): string | null {
  if (!url) return null;
  let path = url.split('#')[0].split('?')[0];
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }
  return path.slice(0, 255);
}

export function recordFailedRequest(
  config: { method?: string; url?: string } | undefined,
  response: { status?: number; headers?: Record<string, unknown> } | undefined
): void {
  const method = (config?.method ?? '').toUpperCase();
  const path = requestPath(config?.url);
  const status = response?.status ?? 0;
  if (!METHODS.has(method) || !path || path.endsWith(OWN_ENDPOINT) || status === 401) return;
  const errorId = response?.headers?.['x-error-id'];
  buffer.push({
    method: method as FailedRequest['method'],
    path,
    status,
    ...(typeof errorId === 'string' && HEX_ID.test(errorId) ? { error_id: errorId } : {}),
    at: new Date().toISOString(),
  });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

export function recentFailedRequests(): FailedRequest[] {
  return buffer.slice();
}

export function trackFailedRequests<T extends { interceptors: { response: { use: Function } } }>(instance: T): T {
  instance.interceptors.response.use(
    (response: unknown) => response,
    (error: { config?: { method?: string; url?: string }; response?: { status?: number; headers?: Record<string, unknown> } }) => {
      recordFailedRequest(error?.config, error?.response);
      return Promise.reject(error);
    }
  );
  return instance;
}
